class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      const hasGiftBox = this.closest('.cart-item').dataset.productHasGiftbox === 'true';
      const hasMonogramProduct = this.closest('.cart-item').dataset.isPersonalised === 'true';

      if (hasGiftBox || hasMonogramProduct) {
        const groupId = this.dataset.groupId;
        cartItems.removeGroupItems(groupId);
      } else {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    // Handle click on "Remove gift box" button in the gift footer
    this.addEventListener('click', (event) => {
      const removeGiftBtn = event.target.closest('.cart-item__gift_box-remove-button-option');
      if (!removeGiftBtn) return;
      event.preventDefault();
      event.stopPropagation();

      const parentRow = removeGiftBtn.closest('.cart-item');
      const groupId = parentRow?.dataset?.groupId;
      if (!groupId) return;

      // Find the gift box container
      const giftBoxContainer = removeGiftBtn.closest('.cart-item__gift_box');
      this.removeGiftBoxAndNote(groupId, giftBoxContainer);
    });
    this.initProgressBar();
  }

  cartUpdateUnsubscriber = undefined;
  isUpdating = false;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });

    // Refresh cart items when page is loaded from cache (back button from checkout)
    const handlePageShow = (event) => {
      // event.persisted is true when page is loaded from cache (back button)
      if (event.persisted) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          if (!this.isUpdating) {
            this.onCartUpdate();
          }
        }, 150);
      }
    };

    // Refresh cart items when page becomes visible (returning from checkout)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay to ensure page is fully loaded, and debounce multiple calls
        setTimeout(() => {
          if (!this.isUpdating) {
            this.onCartUpdate();
          }
        }, 200);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Store cleanup functions
    this.pageNavigationCleanup = () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  /**
   * Remove the gift-box addon line(s) for a given group and clear the Note + gift properties on the main item.
   * Keeps other properties (e.g. personalisation) intact.
   * @param {string|number} groupId
   * @param {HTMLElement} giftBoxContainer - The .cart-item__gift_box container element
   */
  async removeGiftBoxAndNote(groupId, giftBoxContainer) {
    // Pretty logger for cart debugging
    const cartLog = (label, payload) => {
      try {
        // Use a distinct colour to stand out in DevTools
        console.warn('%c[Cart Gift Debug] ' + label, 'color:#0A84FF;font-weight:bold', payload);
      } catch (_) {}
    };

    const fetchCart = async () => {
      const res = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(`${label}/cart.js returned ${res.status}`);
      const data = await res.json();
      return data;
    };

    const postCartChange = async (payload, label = '') => {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Cart change failed: ${label}`);
      return res.json();
    };

    const postCartAdd = async (payload) => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });
    };

    // Enable/disable loading state on the gift box container only
    const toggleGiftBoxLoading = (enable = true) => {
      if (!giftBoxContainer) return;
      
      if (enable) {
        // Ensure container has relative positioning for spinner
        giftBoxContainer.style.position = 'relative';
        giftBoxContainer.classList.add('loading');
        
        // Find or create loading spinner in the gift box container
        let spinner = giftBoxContainer.querySelector('.loading__spinner');
        spinner.classList.remove('hidden');
        
        // Disable buttons during loading
        const buttons = giftBoxContainer.querySelectorAll('.cart-item__gift_box-edit-remove-buttons button');
        buttons.forEach(btn => {
          btn.setAttribute('aria-disabled', 'true');
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.5';
        });
      } else {
        giftBoxContainer.classList.remove('loading');
        const spinner = giftBoxContainer.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');
        }
        
        // Re-enable buttons after loading
        const buttons = giftBoxContainer.querySelectorAll('.cart-item__gift_box-edit-remove-buttons button');
        buttons.forEach(btn => {
          btn.removeAttribute('aria-disabled');
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        });
      }
    };

    try {
      // Enable loading state on the gift box container only
      toggleGiftBoxLoading(true);

      const cart = await fetchCart();
      cartLog('Initial /cart.js before removal', cart);

      // 1) Remove only Gift Box addon lines first (descending so indexes don't shift)
      // Filter to only Gift Box addons to preserve Personalisation addons
      const allAddonLines = this.findAddonProducts(cart, groupId);
      const giftBoxAddonLines = [];
      
      allAddonLines.forEach(line => {
        const item = cart.items[line - 1];
        if (item?.properties?._addonType === 'Gift Box') {
          giftBoxAddonLines.push(line);
        }
      });
      
      const sortedGiftBoxLines = giftBoxAddonLines.sort((a, b) => b - a);
      
      await Promise.all(sortedGiftBoxLines.map(line =>
        postCartChange({ line, quantity: 0 }, `Remove gift box addon line ${line}`)
      ));

      // 2) Re-fetch cart to account for index shifts after removals
      const refreshedCart = await fetchCart();
      cartLog('Cart after addon removal', refreshedCart);

      // 3. Find main product in group (non-addon)
      const mainItemIndex = refreshedCart.items.findIndex(item =>
        String(item.properties?._groupId) === String(groupId) &&
        !item.properties?._addon
      );

      if (mainItemIndex === -1) throw new Error('Main item not found in refreshed cart');

      const mainItem = refreshedCart.items[mainItemIndex];
      cartLog('Main item before cleanup', mainItem);
      const originalLine = mainItemIndex + 1;

      // Check if product has personalization properties or a personalisation addon
      // to preserve _groupId (needed to link main product with personalisation addon)
      const hasPersonalisationProps = Boolean(
        mainItem.properties?.[window.personalisationStrings?.initialsProperty]
      );
      
      // Also check if there's a personalisation addon in the cart with this groupId
      const hasPersonalisationAddon = refreshedCart.items.some(item =>
        item.properties?._addon === 'true' &&
        item.properties?._addonType === 'Personalisation' &&
        String(item.properties?._groupId) === String(groupId)
      );
      
      const hasPersonalisation = hasPersonalisationProps || hasPersonalisationAddon;

      const cleanedProps = Object.entries(mainItem.properties || {})
        .reduce((acc, [key, val]) => {
          const skipKeys = new Set([
            '_hasGiftBox', '_giftboxId',
            '_giftProductHandle', '_giftPid', 'Note'
          ]);
          
          // Always preserve _groupId if personalization exists (needed to link with personalisation addon)
          if (key === '_groupId' && hasPersonalisation) {
            acc[key] = val;
            return acc;
          }
          
          // Skip gift box related keys
          if (!skipKeys.has(key) && val != null && val !== '') {
            acc[key] = val;
          }
          return acc;
        }, {});

      // Remove original item and re-add it with cleaned properties
      await postCartChange({ line: originalLine, quantity: 0 }, 'Remove main item');
      await postCartAdd({ id: mainItem.id, quantity: mainItem.quantity, properties: cleanedProps });

      const finalCart = await fetchCart();
      cartLog('Final /cart.js after cleanup', {
        items: finalCart.items.map((it, idx) => ({ line: idx + 1, id: it.id, key: it.key, properties: it.properties }))
      });
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: finalCart });
      this.onCartUpdate();
      if (window.updateCartItemCount) {
        try { await window.updateCartItemCount(); } catch {}
      }
    } catch (err) {
      const errorsEl = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errorsEl) errorsEl.textContent = window.cartStrings.error;
      console.error(err);
    } finally {
      // Disable loading state on the gift box container
      toggleGiftBoxLoading(false);

      // Always re-render to avoid stale DOM if any step failed
      this.onCartUpdate();
      if (window.updateCartItemCount) {
        try { await window.updateCartItemCount(); } catch {}
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.pageNavigationCleanup) {
      this.pageNavigationCleanup();
    }
  }

  onChange(event) {
    const parent = event.target.closest('.cart-item');
    const hasGiftBox = parent.dataset.productHasGiftbox === 'true';
    const hasMonogramProduct = parent.dataset.isPersonalised === 'true';

    let groupId;

    if (hasGiftBox || hasMonogramProduct) {
      groupId = parent.dataset.groupId;
    }

    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      groupId
    );
  }

  onCartUpdate() {
    // Prevent multiple simultaneous updates
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;

    const sections = this.getSectionsToRender().map((section) => section.section).join(',');
    // Add cache-busting to prevent stale cart data when returning from checkout
    const cacheBuster = `&_=${Date.now()}`;
    fetch(`${routes.cart_url}?sections=${sections}${cacheBuster}`, {
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      }
    })
      .then((response) => {
        if (!response.ok) {
          this.isUpdating = false;
          throw new Error(`Cart update failed: ${response.status}`);
        }
        return response.text();
      })
      .then((state) => {
        let parsedState = {};
        try { parsedState = JSON.parse(state); } catch (_) { 
          this.isUpdating = false;
          return; 
        }
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (!elementToReplace) return;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState[section.section],
            section.selector
          );
        });

        // Reconcile drawer rows against latest cart.js to remove any orphaned addon rows
        // Also verify and remove gift boxes that shouldn't be displayed
        try {
          fetch('/cart.js', { 
            cache: 'no-store',
            headers: { 'X-Requested-With': 'XMLHttpRequest' } 
          })
            .then(r => r.json())
            .then(cart => {
              const addonGroupIds = new Set();
              const giftBoxLineIds = new Set();
              
              cart.items.forEach(it => {
                if (it.properties && it.properties._addon === true && it.properties._groupId) {
                  addonGroupIds.add(String(it.properties._groupId));
                  // Track gift box line items
                  if (it.properties._addonType === 'Gift Box') {
                    giftBoxLineIds.add(String(it.key));
                  }
                }
              });
              
              // Remove orphaned addon rows
              document.querySelectorAll('[data-addon="true"][data-group-id]').forEach(row => {
                const gid = String(row.getAttribute('data-group-id') || '');
                if (!addonGroupIds.has(gid)) {
                  row.remove();
                }
              });
              
              // Verify gift box display: remove gift box UI if the gift box line item doesn't exist in cart
              cart.items.forEach(cartItem => {
                // Only check main items (not addons)
                if (cartItem.properties?._addon === 'true') return;
                
                const groupId = cartItem.properties?._groupId;
                const hasGiftBoxProperty = cartItem.properties?._hasGiftBox === 'true' || 
                                         cartItem.properties?._giftPid ||
                                         cartItem.properties?._giftboxId;
                
                if (hasGiftBoxProperty && groupId) {
                  // Check if there's actually a gift box addon in the cart for this group
                  const hasGiftBoxInCart = cart.items.some(item => 
                    String(item.properties?._groupId) === String(groupId) &&
                    item.properties?._addon === 'true' &&
                    item.properties?._addonType === 'Gift Box'
                  );
                  
                  // If no gift box in cart but properties/UI show it, remove the gift box UI
                  if (!hasGiftBoxInCart) {
                    const cartItemEl = document.querySelector(`.cart-item[data-group-id="${groupId}"]:not([data-addon="true"])`);
                    if (cartItemEl) {
                      const giftBoxCell = cartItemEl.querySelector('.cart-item__gift_box');
                      if (giftBoxCell) {
                        giftBoxCell.remove();
                      }
                      // Update data attribute
                      cartItemEl.setAttribute('data-product-has-giftbox', 'false');
                    }
                  }
                }
              });
              
              // Also check DOM elements that might have stale gift box data
              document.querySelectorAll('.cart-item[data-product-has-giftbox="true"]').forEach(cartItemEl => {
                const groupId = cartItemEl.getAttribute('data-group-id');
                if (groupId) {
                  const hasGiftBoxInCart = cart.items.some(item => 
                    String(item.properties?._groupId) === String(groupId) &&
                    item.properties?._addon === 'true' &&
                    item.properties?._addonType === 'Gift Box'
                  );
                  
                  if (!hasGiftBoxInCart) {
                    const giftBoxCell = cartItemEl.querySelector('.cart-item__gift_box');
                    if (giftBoxCell) {
                      giftBoxCell.remove();
                    }
                    cartItemEl.setAttribute('data-product-has-giftbox', 'false');
                  }
                }
              });
            })
            .catch(() => {});
        } catch(_) {}
        
        // Update cart count after sections are updated
        if (window.updateCartItemCount) {
          window.updateCartItemCount();
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        // Always clear the updating flag
        this.isUpdating = false;
        
        // Ensure any loading states are cleared
        const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) {
          mainCartItems.classList.remove('cart__items--disabled');
        }
        
        // Hide any visible loading spinners
        this.querySelectorAll('.loading__spinner').forEach((spinner) => {
          spinner.classList.add('hidden');
        });
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, groupId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        if (!groupId) {
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;

        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        /**
         * If a Group ID has been passed, find the addon product's cart index so
         * that that product can have it's quantity updated too.
         * - Gets the addon products with the same group ID.
         * - If there is only one addon, remove it.
         * - If there are multiple addons, call `updateQuantity` passing the
         *   `groupId` again to re-check for any remaining addon products to
         *   update.
         */
        if (groupId) {
          const linesToUpdate = this.findAddonProducts(parsedState, groupId);

          const quantityInputs = document.querySelectorAll(
            `[data-addon=true][data-group-id="${groupId}"] .quantity__input`
          );

          if (linesToUpdate.length === 1) {
            this.updateQuantity(linesToUpdate[0], quantity);
          } else if (linesToUpdate.length > 1) {
            this.updateQuantity(linesToUpdate[0], quantity, null, null, groupId);
          }

          quantityInputs.forEach((quantityInput) => {
            quantityInput.value = quantity;
          });
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setTimeout(() => {
          this.disableLoading(line);
        }, 1000);
      });
  }

  /**
   * Remove all cart lines (and any hidden/UI‐only rows) whose properties._groupId matches `groupId`.
   * Uses /cart/change.js one‐by‐one and calls onCartUpdate() after each removal, so the drawer always stays in sync.
   *
   * @param {string|number} groupId  The group ID to match against item.properties._groupId
   */
  async removeGroupItems(groupId) {
    // 1) Show loading spinners on every <.cart-item> with this groupId
    const allCartItemEls = this.querySelectorAll('.cart-item');
    allCartItemEls.forEach(el => {
      if (String(el.dataset.groupId) === String(groupId)) {
        const lineIndex = parseInt(el.dataset.index, 10);
        this.enableLoading(lineIndex);
      }
    });

    try {
      // 2) Fetch /cart.js to see which lines (1-based) share this groupId
      const cartResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!cartResp.ok) throw new Error(`/cart.js returned ${cartResp.status}`);
      const cart = await cartResp.json();

      // 3) Build an array of 1-based lines to delete (where _groupId matches)
      let linesToRemove = [];
      cart.items.forEach((item, idx) => {
        if (
          item.properties &&
          String(item.properties._groupId) === String(groupId)
        ) {
          linesToRemove.push(idx + 1);
        }
      });

      // 4) If nothing matches, disable spinners and exit early
      if (linesToRemove.length === 0) {
        allCartItemEls.forEach(el => {
          const lineIndex = parseInt(el.dataset.index, 10);
          this.disableLoading(lineIndex);
        });
        return;
      }

      // 5) Sort descending so removing a higher line first doesn't shift indexes for the next lines
      linesToRemove.sort((a, b) => b - a);

      // 6) Loop through each line, remove it via /cart/change.js, then call onCartUpdate()
      for (const lineNum of linesToRemove) {
        const changeBody = JSON.stringify({ line: lineNum, quantity: 0 });

        const changeResp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: changeBody
        });

        if (!changeResp.ok) {
          let errJson = {};
          try { errJson = await changeResp.json(); } catch {}
          throw new Error(
            `/cart/change.js failed for line ${lineNum}: ${changeResp.status} – ${JSON.stringify(errJson)}`
          );
        }

        const updatedCart = await changeResp.json();

        // 7) Publish cartUpdate so mini‐cart badges, etc. refresh immediately
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: updatedCart
        });

        // 8) Call onCartUpdate() so your drawer (or main‐cart page) replaces itself with the latest HTML
        //    This covers both scenarios: if user is in a drawer, it re-fetches `?section_id=cart-drawer`; 
        //    if on the main-cart page, it re-fetches `?section_id=main-cart-items`.
        this.onCartUpdate();

        // 9) Immediately disable the spinner on the line we just removed
        this.disableLoading(lineNum);
        
        // 10) Short pause to let the drawer re-render before removing the next line (optional, but smoother)
        await new Promise(r => setTimeout(r, 150));
      }

      // 11) After removing all lines for that group, we may still have “hidden/UI-only” rows in the DOM.
      //     Purge them now: any <.cart-item data-group-id="…"> left over (they never existed in /cart.js).
      document
        .querySelectorAll(`.cart-item[data-group-id="${groupId}"]`)
        .forEach(el => el.remove());

      // 12) Finally, fetch the cart one more time to know if it’s empty (so we can trap focus appropriately).
      const finalCartResp = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!finalCartResp.ok) throw new Error(`/cart.js returned ${finalCartResp.status} after final removals`);
      const finalCart = await finalCartResp.json();

      // 13) If the drawer still exists, toggle its “is-empty” class; otherwise skip
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('is-empty', finalCart.item_count === 0);
      }

      // 14) If the main‐cart‐footer exists, toggle its “is-empty” too
      const footer = document.getElementById('main-cart-footer');
      if (footer) {
        footer.classList.toggle('is-empty', finalCart.item_count === 0);
      }

      if (window.updateCartItemCount) {
        await window.updateCartItemCount();
      }

      // 15) Trap focus to the “empty” container if cart is now empty; otherwise to first item
      if (finalCart.item_count === 0 && drawer) {
        const emptyContainer = drawer.querySelector('.drawer__inner-empty');
        const fallbackLink = drawer.querySelector('a');
        if (emptyContainer && fallbackLink) {
          trapFocus(emptyContainer, fallbackLink);
        }
      } else if (document.querySelector('.cart-item') && drawer) {
        trapFocus(drawer, document.querySelector('.cart-item__name'));
      }

      // 16) Publish one more cartUpdate so any other parts of your theme (like the mini‐cart count badge)
      //     have the final state.
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: finalCart
      });
    }
    catch (err) {
      // 17) On any error (network or API), disable all spinners and show a generic error
      allCartItemEls.forEach(el => {
        const lineIndex = parseInt(el.dataset.index, 10);
        this.disableLoading(lineIndex);
      });
      const errorsEl =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');
      if (errorsEl) {
        errorsEl.textContent = window.cartStrings.error;
      }
    }
  }


  /**
   * Find the line numbers for addon products with a matching group id.
   * - Does this by finding a product with `_addon: true` and `_groupId`
   *   property that matches the passed `groupId`.
   * @param {Object} state - Cart state
   * @param {String|Number} groupId - Line item property value that groups main products
   * with their addon products.
   * @returns {Array<Number>}
   */
  findAddonProducts(state, groupId) {
    const addonLines = [];
    state.items.forEach((item, index) => {
      if (item.properties && item.properties._addon && parseInt(item.properties._groupId) === parseInt(groupId)) {
        const line = index + 1;
        addonLines.push(line);
      }
    });

    return addonLines;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  initProgressBar() {
    this.fetchCartTotal()
      .then((cartTotal) => {
        if (cartTotal > 0) {
          const formattedMoney = (cartTotal / 100).toFixed(2);
          this.updateProgressBar(formattedMoney);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  fetchCartTotal() {
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        let cartTotal = 0;

        cart.items.forEach((item) => {
          if (!item.gift_card) {
            cartTotal += item.line_price;
          }
        });
        return cartTotal;
      });
  }

  updateProgressBar(cartTotal) {
    const progressBar = document.getElementById('shippingProgressBar');
    const cartInfo = document.getElementById('cart-info');
    const freeShippingThreshold = parseFloat(cartInfo?.dataset?.freeShippingThreshold);

    if (freeShippingThreshold) {
      const percentage = (cartTotal / freeShippingThreshold) * 100;

      progressBar.style.width = `${percentage}%`;
      progressBar.setAttribute('aria-valuenow', cartTotal);
      progressBar.setAttribute('aria-valuemax', freeShippingThreshold);
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
